import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  IconButton,
  Link,
} from '@mui/material';
import { AttachFile, Send, Close } from '@mui/icons-material';
import { format } from 'date-fns';
import { tasksAPI } from '../../services/api';
import { toast } from 'react-toastify';

const TaskDetails = ({ open, onClose, taskId, onEdit }) => {
  const [task, setTask] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (taskId && open) {
      fetchTaskDetails();
    }
  }, [taskId, open]);

  const fetchTaskDetails = async () => {
    try {
      const response = await tasksAPI.getOne(taskId);
      setTask(response.data.data);
    } catch (err) {
      toast.error('Failed to load task details');
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    try {
      setLoading(true);
      await tasksAPI.addComment(taskId, { text: comment });
      setComment('');
      fetchTaskDetails();
      toast.success('Comment added');
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{task.title}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Description
          </Typography>
          <Typography variant="body1">{task.description || 'No description'}</Typography>
        </Box>

        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">
              Priority
            </Typography>
            <Box>
              <Chip label={task.priority} color="primary" size="small" />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <Chip label={task.status} size="small" />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Deadline
            </Typography>
            <Typography variant="body2">
              {format(new Date(task.deadline), 'MMM dd, yyyy')}
            </Typography>
          </Box>
        </Box>

        {task.attachments && task.attachments.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Attachments
            </Typography>
            <List dense>
              {task.attachments.map((file, idx) => (
                <ListItem key={idx}>
                  <AttachFile fontSize="small" sx={{ mr: 1 }} />
                  <Link href={`https://prj.trackcord.com/${file.path}`} target="_blank">
                    {file.originalName}
                  </Link>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {task.reminders && task.reminders.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Reminders
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {task.reminders.map((reminder, idx) => (
                <Chip
                  key={idx}
                  label={`${reminder.type} ${reminder.sent ? '(sent)' : ''}`}
                  size="small"
                  color={reminder.sent ? 'default' : 'primary'}
                />
              ))}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Comments
        </Typography>
        <List>
          {task.comments && task.comments.length > 0 ? (
            task.comments.map((comment, idx) => (
              <ListItem key={idx} alignItems="flex-start">
                <ListItemText
                  primary={comment.user?.name || 'Unknown'}
                  secondary={
                    <>
                      <Typography variant="body2">{comment.text}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(comment.createdAt), 'MMM dd, yyyy hh:mm a')}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No comments yet
            </Typography>
          )}
        </List>

        <Box display="flex" gap={1} mt={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Add a comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddComment();
              }
            }}
          />
          <IconButton color="primary" onClick={handleAddComment} disabled={loading}>
            <Send />
          </IconButton>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onEdit(task)}>Edit Task</Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDetails;
